import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { useCallback, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import SearchModal, { RECENTLY_SEARCHED_KEY } from '../modals/SearchModal'
import { useViewport } from 'hooks/useViewport'
import { IconButton } from '../shared/Button'
import store from 'stores/store'
import { TokenMetadataItem, TokenMinimalData } from 'types/token'
import { fetchMetadataForMints } from 'utils/tokens'
import { useRunOnceEffect } from 'hooks/useRunOnceEffect'
import { useTranslation } from 'next-i18next'

const metadataCache = new Map<string, TokenMetadataItem[]>()

function isKeyboardEditableElement(element: Element | null) {
  if (
    !element ||
    (!(element instanceof HTMLInputElement) &&
      !(element instanceof HTMLTextAreaElement) &&
      !element.hasAttribute('contenteditable'))
  ) {
    return false
  }

  return !(
    element instanceof HTMLInputElement &&
    [
      'button',
      'submit',
      'checkbox',
      'radio',
      'file',
      'hidden',
      'image',
      'range',
      'reset',
    ].indexOf(element.type) !== -1
  )
}

const HeroSearch = () => {
  const { isDesktop } = useViewport()
  const [showSearchModal, setShowSearchModal] = useState<
    true | false | 'focus-search'
  >(false)
  const router = useRouter()
  const [recentlySearched, setRecentlySearched] = useLocalStorage(
    RECENTLY_SEARCHED_KEY,
  )
  const setMarketAndSwapToken = store((s) => s.actions.setMarketAndSwapToken)
  const { t } = useTranslation('search')

  useRunOnceEffect(
    'update-recently-searched',
    recentlySearched,
    async () => {
      if (!recentlySearched.length) {
        return []
      }
      return (
        await fetchMetadataForMints(
          recentlySearched.map(({ mint }) => mint),
          metadataCache,
          store.getState().connection,
        )
      ).map(
        ({ mint, name, image_url, symbol }): TokenMinimalData => ({
          mint,
          name,
          image_url,
          symbol,
        }),
      )
    },
    (updatedTokens) =>
      setRecentlySearched(
        recentlySearched.map(
          (token) => updatedTokens.find((t) => t.mint === token.mint) ?? token,
        ),
      ),
  )

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key !== '/') {
        return
      }

      const activeElement = document.activeElement
      if (isKeyboardEditableElement(activeElement)) {
        return
      }

      e.preventDefault()
      setShowSearchModal('focus-search')
    }

    window.addEventListener('keydown', handleKeyPress)

    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [])

  const handleResultClick = useCallback(
    async (mint: string, token?: TokenMetadataItem) => {
      const isRecentlySearched = recentlySearched.find((t) => t.mint === mint)
      if (!isRecentlySearched) {
        const { image_url, name, symbol } =
          token ??
          (
            await fetchMetadataForMints(
              [mint],
              metadataCache,
              store.getState().connection,
            )
          )[0]
        recentlySearched.unshift({ mint, image_url, name, symbol })
        setRecentlySearched(recentlySearched.slice(0, 3))
      }
      setMarketAndSwapToken(mint, undefined, token)
      void router.push(`/token/${mint}`)
      setShowSearchModal(false)
    },
    [recentlySearched, setMarketAndSwapToken, setRecentlySearched, router],
  )

  return (
    <>
      {isDesktop ? (
        <button
          className="flex h-12 items-center justify-between rounded-full bg-th-bkg-3 px-4 text-left text-sm font-normal text-th-fgd-1 focus:outline-none focus-visible:border-th-fgd-4 md:text-base md:hover:bg-th-bkg-4 md:hover:focus-visible:border-th-fgd-4 xl:w-72"
          onClick={() => setShowSearchModal(true)}
        >
          <div className="flex items-center pr-2">
            <MagnifyingGlassIcon className="mr-2 size-5 shrink-0 text-th-fgd-2" />
            <span className="text-sm text-th-fgd-4">{t('search-tokens')}</span>
          </div>
          <div className="flex h-6 w-5 items-center justify-center rounded border border-th-bkg-4 bg-th-bkg-1">
            <span className="text-sm font-semibold leading-none text-th-fgd-4">
              /
            </span>
          </div>
        </button>
      ) : (
        <IconButton
          className="bg-th-bkg-2 md:hover:bg-th-bkg-3"
          onClick={() => setShowSearchModal(true)}
          size="small"
        >
          <MagnifyingGlassIcon className="size-5 text-th-fgd-1" />
        </IconButton>
      )}
      {showSearchModal ? (
        <SearchModal
          isOpen={!!showSearchModal}
          onClose={() => setShowSearchModal(false)}
          onResultClick={handleResultClick}
          type="search"
          focusSearchOnShow={showSearchModal === 'focus-search'}
        />
      ) : null}
    </>
  )
}

export default HeroSearch
